import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { mergeAll } from 'rxjs/operators';

import { IDynamicLayerSpatial, ILayer, IQuery, ISpatialQueryParams, isQueryableMapCoreLayer, MapCoreLayer } from '../../src/types';
import { LayerApi } from '../api/layer-api';
import { SpatialApi } from '../api/spatial-api';
import { BoundaryType } from '../components/filterComponents/geographic-filter/geographic-filter.component';
import { environment } from '../environment';
import { ESRI } from '../esri-loader-util';

import { GisToolsService } from './gis-tools.service';
import { LayerService } from './layer.service';
import { ToSqlStringService } from './to-sql-string.service';

@Injectable({
    providedIn: 'root'
})
export class SpatialQueryService {
    constructor(
        private readonly spatialApi: SpatialApi,
        private readonly layerApi: LayerApi,
        private readonly layerService: LayerService,
        private readonly gisToolsService: GisToolsService,
        private readonly toSqlStringService: ToSqlStringService
    ) { }

    async getLayerGeometry(featureSet: __esri.FeatureSet) {

        if (featureSet.geometryType.toLowerCase() === 'point') {
            const [Point, Multipoint] = await Promise.all([ESRI.Point, ESRI.Multipoint]);
            const multipoints: __esri.geometry.Multipoint = new Multipoint({
                spatialReference: featureSet.features[0].geometry.spatialReference
            });
            featureSet.features.forEach((graphic: __esri.Graphic) => {
                const point: __esri.geometry.Point = new Point(graphic.geometry);
                multipoints.addPoint(point);
            });
            return multipoints;
        } else if (featureSet.geometryType.toLowerCase() === 'polyline' || featureSet.geometryType.toLowerCase() === 'line') {
            const [Polyline] = await Promise.all([ESRI.Polyline]);
            const singlePolyline: __esri.geometry.Polyline = new Polyline({
                spatialReference: featureSet.features[0].geometry.spatialReference
            });
            featureSet.features.forEach((graphic: __esri.Graphic) => {
                const line: __esri.geometry.Polyline = new Polyline(graphic.geometry);
                if (line.paths.length > 0) {
                    singlePolyline.paths = singlePolyline.paths.concat(line.paths);
                }
            });
            return singlePolyline;
        } else if (featureSet.geometryType.toLowerCase() === 'polygon') {
            const [Polygon] = await Promise.all([ESRI.Polygon]);
            const singlePolygon: __esri.geometry.Polygon = new Polygon({
                spatialReference: featureSet.features[0].geometry.spatialReference
            });
            featureSet.features.forEach((graphic: any) => {
                const polygon: __esri.geometry.Polygon = new Polygon(graphic.geometry);
                if (polygon.rings.length > 0) {
                    singlePolygon.rings = singlePolygon.rings.concat(polygon.rings);
                }
            });
            return singlePolygon;
        }
    }

    async filterWithGeometry(url, oidField, geometry) {
        const [Query, QueryTask] = await Promise.all([ESRI.Query, ESRI.QueryTask]);
        const queryTask: __esri.QueryTask = new QueryTask({
            url
        });
        const query: __esri.Query = new Query({
            outFields: [oidField],
            geometry,
            spatialRelationship: 'intersects',
            returnGeometry: false
        });

        const queryResult = await queryTask.execute(query);
        return queryResult;
    }

    //Li is still working on the guid part so this is working as-is for now. Once the guid stuff is set up,
    //we will use that instead of returning the objectids here.
    async filterLayerWithGeometry(sublayer, geometry) {
        const query = sublayer.createQuery();
        query.outFields = [sublayer.source.dataSource.oidFields];
        query.geometry = geometry;
        query.spatialRelationship = 'intersects';
        query.returnGeometry = false;
        const features = await sublayer.queryFeatures(query);
        const outField = query.outFields[0].toUpperCase();
        return this.getObjectIdList(features.features, outField);
    }

    getObjectIdList(features: __esri.Graphic[], objectIdField: string) {
        const objectIds: any[] = [];
        features.forEach((feature: __esri.Graphic) => {
            objectIds.push(feature.attributes[objectIdField]);
        });
        return objectIds;
    }

    /** Gets a GUID query object given a guid string */
    getGuidQuery(guid: string, name: string = null): IQuery {
        const query: IQuery = {
            field: 'OBJECTID',
            operator: 'GUID',
            value: guid
        };
        if (name) {
            query.name = name;
        }
        return query;
    }

    /** Gets a query object given a objectId list */
    getQueryByObjectIds(objectIds: number[], name: string = null): IQuery {
        const query: IQuery = {
            field: 'OBJECTID',
            operator: 'IN',
            isMultiple: true,
            multiValue: objectIds.map(String)
        };
        if (name) {
            query.name = name;
        }
        return query;
    }

    /** Gets a specific type of object for filtering dynamic layers spatially */
    getIDynamicLayerSpatialFromLayer(layer: any) {
        const sublayers = [];
        let queries: IQuery;
        layer.sublayers.forEach(sublayer => {
            if (layer.mapcoreProps) {
                queries = this.toSqlStringService.getSimplifiedDeepCopyOfQuery(layer.mapcoreProps.query, false);
            } else {
                queries = this.toSqlStringService.getSimplifiedDeepCopyOfQuery(sublayer.mapcoreProps.query, false);
            }
            let hasParentQuery = false;
            if (queries && queries.subqueries && queries.subqueries.length > 0 && layer.mapcoreProps.parentQuery && layer.mapcoreProps.parentQuery.field) {
                hasParentQuery = queries.subqueries.filter(x => x.field === layer.mapcoreProps.parentQuery.field && x.operator === layer.mapcoreProps.parentQuery.operator && x.value === layer.mapcoreProps.parentQuery.value).length > 0;
            }
            if (layer.mapcoreProps.parentQuery && !hasParentQuery) {
                queries = this.toSqlStringService.addFilter(queries, layer.mapcoreProps.parentQuery);
            }
            sublayers.push({
                id: sublayer.id,
                source: sublayer.source,
                query: queries
            });
        });
        return {
            id: layer.id,
            sublayers
        };
    }

    /**
     * Add or update spatial query result to layer
     */
    async handleResults(results, spParams, isNewLayer: boolean, isIntersect: boolean, title: string = '', suffixOverride?: string, renewLayerId?: string) {
        results.forEach(async result => {
            const layer = this.layerService.getEsriLayerByUniqueId(renewLayerId ? renewLayerId : result.id);
            let query: IQuery;
            const spatialQueryParams = { ...spParams };
            spatialQueryParams.primaryDynamicLayers = [...spParams.primaryDynamicLayers];
            spatialQueryParams.primaryFeatureLayers = [...spParams.primaryFeatureLayers];
            if (result.guid) {
                spatialQueryParams.guid = result.guid;
            } else {
                spatialQueryParams.objectIds = result.objectIds;
            }
            if (!renewLayerId) {
                this.filterSelectedLayers(this.GetMapLayerList([layer]), spatialQueryParams.primaryDynamicLayers, spatialQueryParams.primaryFeatureLayers);
            }
            if (isNewLayer) {
                let newTitle = title;
                if (title === '') {
                    newTitle = layer.mapcoreProps.name as string;
                    newTitle += suffixOverride ? ` - ${suffixOverride}` : isIntersect ? ' - Intersect' : ' - Difference';
                }
                let esriLayer: __esri.Layer;
                let newQuery = this.toSqlStringService.getSimplifiedDeepCopyOfQuery(layer.mapcoreProps.query);
                let parentQuery;
                if (layer.mapcoreProps.spatialQueryParams) {
                    if (result.guid) {
                        newQuery = this.updateQueryWithGUID(newQuery, result.guid, layer.mapcoreProps.spatialQueryParams.guid);
                    } else {
                        newQuery = this.updateQueryWithObjectIds(newQuery, result.objectIds, layer.mapcoreProps.spatialQueryParams.objectIds);
                    }
                } else {
                    if (result.guid) {
                        newQuery = this.updateQueryWithGUID(newQuery, result.guid);
                    } else if (result.objectIds) {
                        newQuery = this.updateQueryWithObjectIds(newQuery, result.objectIds);

                    }
                }

                if (layer.mapcoreProps.parentQuery) {
                    parentQuery = { ...layer.mapcoreProps.parentQuery };
                }
                const layerType = this.layerService.getLayerType(layer);
                if (layerType === 'dynamic') {
                    const layerSettings = { id: layer.mapcoreProps.parentId, name: newTitle, query: newQuery, spatialQueryParams, parentQuery };
                    const esriLayers: __esri.Layer[] = await this.layerService.addMapcoreLayersToMap([layerSettings]);
                    esriLayer = esriLayers[0];
                    spatialQueryParams.primaryDynamicLayers[0].id = esriLayer.id;
                } else if (layerType === 'feature') {
                    const mapcoreProps = { ...layer.mapcoreProps };
                    mapcoreProps.name = newTitle;
                    mapcoreProps.query = newQuery;
                    mapcoreProps.spatialQueryParams = spatialQueryParams;
                    esriLayer = await this.layerService.addExternalLayersToMap({ ...mapcoreProps });
                    spatialQueryParams.primaryFeatureLayers[0].id = esriLayer.id;
                }
                const newLayer = this.layerService.getEsriLayerByUniqueId(esriLayer.id);
                newLayer.mapcoreProps.spatialQueryParams = spatialQueryParams;
                if (isQueryableMapCoreLayer(newLayer)) {
                    newLayer.refresh();
                }
            } else if (isQueryableMapCoreLayer(layer)) {
                if (layer.mapcoreProps.spatialQueryParams) {
                    query = this.updateLayerWithGUID(layer, result.guid, layer.mapcoreProps.spatialQueryParams.guid);
                } else {
                    query = this.updateLayerWithGUID(layer, result.guid);
                }
                this.layerService.updateLayerFilter(layer, query);
                layer.mapcoreProps.spatialQueryParams = spatialQueryParams;
                layer.refresh();
            }
        });
    }

    /**
     * Service used to renew existing spatial queried layer
     * Add layerId as optional for now in case some other applications other than Onemap and Maintenance IQ uses it.
     */
    async renewQuery(queryParams: ISpatialQueryParams, layerId?: string) {
        const newLayer = false, id = layerId, isIntersect = queryParams.isIntersect, primaryDynamicLayers = queryParams.primaryDynamicLayers,
            primaryFeatureLayers = queryParams.primaryFeatureLayers, joinedDynamicLayers = queryParams.joinedDynamicLayers,
            joinedFeatureLayers = queryParams.joinedFeatureLayers, joinedGeometries = queryParams.joinedGeometries,
            processingType = queryParams.processingType, srMatch = queryParams.srMatch;
        const spatialQueryParams: ISpatialQueryParams = {
            primaryDynamicLayers,
            primaryFeatureLayers,
            joinedDynamicLayers,
            joinedFeatureLayers,
            joinedGeometries,
            isIntersect,
            guid: '',
            processingType,
            srMatch
        };
        let dynamicResults: { id: string; guid: string; resultCount?: number }[] = [];
        let featureResults: { id: string; objectIds: number[]; resultCount?: number }[] = [];
        if (primaryDynamicLayers && primaryDynamicLayers.length && primaryDynamicLayers.length > 0) {
            dynamicResults = await this.spatialQuery(isIntersect, primaryDynamicLayers, [], joinedDynamicLayers, joinedFeatureLayers, joinedGeometries, processingType, srMatch);
        }
        if (primaryFeatureLayers && primaryFeatureLayers.length && primaryFeatureLayers.length > 0) {
            featureResults = await this.spatialQuery(isIntersect, [], primaryFeatureLayers, joinedDynamicLayers, joinedFeatureLayers, joinedGeometries, processingType, srMatch, true);
        }
        const results = [...dynamicResults, ...featureResults];
        //const results: { id: string; guid?: string; objectIds?: number[]; resultCount: number }[] = await this.spatialQuery(isIntersect, primaryDynamicLayers, primaryFeatureLayers, joinedDynamicLayers, joinedFeatureLayers, joinedGeometries, processingType, srMatch);
        await this.handleResults(results, spatialQueryParams, newLayer, isIntersect, null, null, id);
        return results;
    }

    /**
     * Add current layers on the map in the format that spatial API accepts
     */
    GetMapLayerList(layers, isPrimaryLayer = false) {
        const maplayerList: any[] = [];
        for (const layer of layers) {
            if (layer.mapcoreProps.hideInLegend) {
                continue;
            } else if (this.layerService.getLayerType(layer) === 'group') {
                for (const sub of layer.layers) {
                    const maplayer = {
                        id: sub.id,
                        name: sub.mapcoreProps.name,
                        title: sub.title,
                        type: sub.type,
                        mapcoreProps: sub.mapcoreProps,
                        sublayers: [],
                        parsedUrl: ''
                    };
                    if (this.layerService.getLayerType(layer) === 'dynamic') {
                        maplayer.sublayers = sub.sublayers.toArray();
                    } else if (this.layerService.getLayerType(layer) === 'feature') {
                        maplayer.parsedUrl = sub.parsedUrl;
                    }
                    maplayerList.push(maplayer);
                }
            } else {
                const maplayer = {
                    id: layer.id,
                    name: layer.mapcoreProps.name,
                    title: layer.title,
                    type: layer.type,
                    mapcoreProps: layer.mapcoreProps,
                    sublayers: [],
                    parsedUrl: '',
                    graphics: []
                };
                if (this.layerService.getLayerType(layer) === 'dynamic') {
                    maplayer.sublayers = layer.sublayers.toArray();
                } else if (this.layerService.getLayerType(layer) === 'feature') {
                    maplayer.parsedUrl = layer.parsedUrl;
                } else if (!isPrimaryLayer && this.layerService.getLayerType(layer) === 'graphics') {
                    maplayer.graphics = layer.graphics;
                }
                maplayerList.push(maplayer);
            }
        }
        return maplayerList;
    }

    /**
     * Prepare layers for spatialQueryParams
     */
    public filterSelectedLayers(layers, dynamicLayers: IDynamicLayerSpatial[] = null, featureLayers: ILayer[] = null, graphicsLayers: string[] = null) {

        layers.forEach((layer: any) => {
            switch (this.layerService.getLayerType(layer)) {
                case 'dynamic': {
                    if (dynamicLayers) {
                        const dynamicLayerSpatial = this.getIDynamicLayerSpatialFromLayer(layer);
                        if (dynamicLayers.filter(x => x.id === dynamicLayerSpatial.id).length === 0) {
                            dynamicLayers.push(dynamicLayerSpatial);
                        }
                    }
                    break;
                }
                case 'feature': {
                    if (featureLayers) {
                        if (featureLayers.filter(x => x.id === layer.id).length === 0) {
                            featureLayers.push({
                                id: layer.id,
                                url: layer.parsedUrl.path,
                                query: layer.mapcoreProps && layer.mapcoreProps.query ? layer.mapcoreProps.query : null
                            });
                        }
                    }
                    break;
                }
                case 'graphics': {
                    if (graphicsLayers) {
                        layer.graphics.forEach((graphic) => {
                            if (graphic.geometry.type === 'polygon') {
                                graphic.geometry = this.formatPolygonGeometry(graphic.geometry);
                            }
                            graphicsLayers.push(JSON.stringify(graphic.geometry));
                        });
                    }
                    break;
                }
                default: break;
            }
        });
    }

    /**
     * Add GUID to the layer filter
     * @param layer MapCore layer object to filter by the spatial results GUID
     * @param guid MapCore spatial query GUID value
     * @param existingGuid Previous GUID present in the query that should be replaced with the new GUID
     */
    public updateLayerWithGUID(layer, guid, existingGuid = '') {
        return this.updateQueryWithGUID(layer.mapcoreProps.query, guid, existingGuid);
    }

    /**
     * Add GUID to query
     * @param query MapCore query object
     * @param guid MapCore spatial query GUID value
     * @param existingGuid Previous GUID present in the query that should be replaced with the new GUID
     */
    public updateQueryWithGUID(query, guid, existingGuid = '') {
        query = query || {};
        if (existingGuid === '') {
            const guidQuery = this.getGuidQuery(guid, null);
            return this.toSqlStringService.addFilter(query, guidQuery);
        } else {
            return this.toSqlStringService.replaceGUID(query, existingGuid, guid);
        }
    }

    /**
     * Add objectids to query
     * @param query MapCore query object
     * @param objectIds MapCore spatial objectId results
     * @param existingObjectIds Previous object Ids present in the query that should be replaced with new objectids
     */
    public updateQueryWithObjectIds(query, objectIds, existingobjectIds = []) {
        query = query || {};
        if (existingobjectIds && existingobjectIds.length > 0) {
            return this.toSqlStringService.replaceObjQuery(query, existingobjectIds.toString(), objectIds);
        } else {
            const tempQuery = this.getQueryByObjectIds(objectIds, null);
            return this.toSqlStringService.addFilter(query, tempQuery);
        }
    }
    public async applyGeographicFilter(esriLayer, geometryString: string) {
        const layerSpatial = this.getIDynamicLayerSpatialFromLayer(esriLayer);
        const results = await this.spatialQuery(true, [layerSpatial], null, null, null, [geometryString]);
        const spatialQueryParams = {
            primaryDynamicLayers: [],
            primaryFeatureLayers: [],
            joinedDynamicLayers: [],
            joinedFeatureLayers: [],
            geometryString,
            isIntersect: true,
            guid: ''
        };
        await this.handleResults(results, spatialQueryParams, false, true);

    }

    //this is a temporary fix for polygon geometries. Ruidong will be updating the polygon draw tool to do this instead
    formatPolygonGeometry(geometry: any) {
        geometry.rings.forEach(ring => {
            //add first point to last of the array to close the polygon
            ring.push(ring[0]);
        });
        return geometry;
    }

    /**
     * Converts an Oracle polygon Geometry string to an ESRI based polygon string
     */
    getGeometryStringFromOraclePolygonString(oracleGeometryStrings: string[]) {
        let polygonPointStrings = [];
        for (const item of oracleGeometryStrings) {
            let itemArray = item.split(')), ((');
            itemArray = itemArray.map(x => x.replace('MULTIPOLYGON ((', '').replace('POLYGON ((', '').replace('))', '').replace('(', '').replace(')', ''));
            polygonPointStrings = polygonPointStrings.concat(itemArray);
        }
        const ringsArray = polygonPointStrings.map(x => x.split(', ').map(y => y.split(' ')));
        const esriPolygon = { rings: ringsArray };
        return JSON.stringify(esriPolygon);
    }

    public async spatialQuery(isIntersect: boolean, primaryDynamicLayers?: IDynamicLayerSpatial[], primaryFeatureLayers?: ILayer[], joinedDynamicLayers?: IDynamicLayerSpatial[], joinedFeatureLayers?: ILayer[], joinedGeometries?: string[], processingType?: string, srMatch = true, returnOID = false) {
        if (returnOID) {
            return await this.spatialApi.spatialQuery_OID(isIntersect, primaryDynamicLayers, primaryFeatureLayers, joinedDynamicLayers, joinedFeatureLayers, joinedGeometries, processingType, srMatch);
        } else {
            return await this.spatialApi.spatialQuery(isIntersect, primaryDynamicLayers, primaryFeatureLayers, joinedDynamicLayers, joinedFeatureLayers, joinedGeometries, processingType, srMatch);
        }
    }

    /**
     *  Run a spatial query for each layer returning results/GUIDs once all queries are completed.
     *  Similar to spatialQuery() but accepts layers and joinedLayers arrays with supported layer types respectively.
     *  @param layers Source layers/features for the query
     *  @param joinedLayers Layers to filter source features by
     */
    public async spatialQueryLayers(isIntersect: boolean, layers: MapCoreLayer[], joinedLayers: MapCoreLayer[], processingType = 'default', srMatch = true): Promise<any> {
        const primaryDynamicLayers: IDynamicLayerSpatial[] = [];
        const primaryFeatureLayers: ILayer[] = [];
        this.filterSelectedLayers(layers, primaryDynamicLayers, primaryFeatureLayers);
        const joinedDynamicLayers: IDynamicLayerSpatial[] = [];
        const joinedFeatureLayers: ILayer[] = [];
        const joinedGeometries: string[] = [];
        this.filterSelectedLayers(joinedLayers, joinedDynamicLayers, joinedFeatureLayers, joinedGeometries);
        return this.spatialQuery(
            isIntersect,
            primaryDynamicLayers,
            primaryFeatureLayers,
            joinedDynamicLayers,
            joinedFeatureLayers,
            joinedGeometries,
            processingType,
            srMatch);
    }

    /**
     *  Run separate spatial queries for each layer returning results/GUIDs as they finish in an Observable.
     *
     *  * Use spatialQuery() or spatialQueryLayers() instead if processing can wait until
     *    all results are returned.
     *
     *  * Use spatialQueryInParallel() if the user needs updated when individual queries are completed.
     *
     *  Example:
     *
     *            await this.spatialQueryInParallel(layers, aoiLayers).forEach(result => console.log(result));
     *
     *  @param layers Source layers/features for the query
     *  @param joinedLayers Layers to filter source features by
     */
    public spatialQueryInParallel(isIntersect: boolean, layers: MapCoreLayer[], joinedLayers: MapCoreLayer[], processingType, srMatch): Observable<any> {
        const queries = layers.map(layer => this.spatialQueryLayers(
            isIntersect,
            [layer],
            joinedLayers,
            processingType,
            srMatch)
        );
        return from(queries).pipe(mergeAll());
    }

    public async getGeometry(selectedFeatures: any[], selectedBoundaryType: BoundaryType) {
        let geometries: string[];
        let boundaryFeatures: any[];
        if (selectedFeatures && selectedFeatures.length && selectedFeatures.length > 0) {
            if (!selectedFeatures[0].geometry) {
                if (selectedBoundaryType.apiQueryName === 'assistantAreasWinter' || selectedBoundaryType.apiQueryName === 'assistantAreasSummer') {
                    const results = (await this.layerApi.getData(this.getLayerIdByName(selectedBoundaryType.apiQueryName), ['geometry'], { field: 'objectid', operator: 'IN', value: selectedFeatures.map(x => x.objectId).toString() }));
                    geometries = results.flat();
                } else {
                    boundaryFeatures = await this.gisToolsService.getFeaturesByObjectId(selectedBoundaryType.apiQueryName, 'geometry', selectedFeatures.map(x => x.objectId));
                    geometries = boundaryFeatures.map(x => x.geometry);
                }
            } else {
                geometries = selectedFeatures.map(x => x.geometry);
            }
            const geometryString = this.getGeometryStringFromOraclePolygonString(geometries);
            return geometryString;
        } else {
            return Promise.resolve(null);

        }
    }

    public getLayerIdByName(name: string) {
        return environment.layers.filter(x => x.name === name)[0].id;
    }

}
